import axios from 'axios'
import store from '../store'
import { resetState } from '../store/redux-slices'
import { getCookie, getItem, getLoginToken } from './storageUtils'
import { setIsUserBlock } from '../store/redux-slices/user'
import { closeAllModals } from '../store/redux-slices/modal'
import { disconnectSockets } from './session'

const BASE_URL = process.env.REACT_APP_API_URL || ''

const axiosInstance = axios.create({
  baseURL: BASE_URL + '/api'
})

export const setupInterceptors = () => {
  axiosInstance.interceptors.response.use(
    (res) => res,
    (error) => {
      if (error.response.status === 403) {
        if (window?.cioLogoutUser) {
          window.cioLogoutUser()
        }
        // The session is gone server-side: close the sockets opened with its token first, otherwise wallet
        // pushes keep driving authenticated calls (and more 403s) from a tab that is already logged out
        disconnectSockets()
        store.dispatch(resetState())
      } else if (error.response.status === 406) {
        window.location.href = '/tenant-inactive'
      } else if (error.response.status === 600) {
        store.dispatch(setIsUserBlock(true))
        store.dispatch(closeAllModals())
      }
      return Promise.reject(error)
    }
  )
}

const METHODS = {
  get: 'GET',
  post: 'POST',
  put: 'PUT',
  delete: 'DELETE'
}

const makeRequest = async (endpoint, method = METHODS.get, params = {}, data = {}, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.customHeaders
  }

  if (getLoginToken() && !options.ignoreAuthorization) {
    headers.Authorization = `Bearer ${getLoginToken()}`
  }

  headers.language = getItem('language') || 'EN'

  if (getCookie('countryCode')) {
    headers['Country-Code-Id'] = getCookie('countryCode') || ''
  }

  // This will serialize function is to handle to pass array query params
  // paramsSerializer: Pass arrays as arrays in params (e.g., { user: ['id1', 'id2'] })
  const paramsSerializer = (params) => {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length === 0) return // skip empty arrays
        value.forEach((v) => searchParams.append(`${key}[]`, v))
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, value)
      }
    })
    return searchParams.toString()
  }

  const config = {
    url: endpoint,
    method,
    headers,
    paramsSerializer,
    ...options
  }

  if (method === METHODS.get) {
    config.params = params
  } else {
    config.data = data
    if (Object.keys(params).length) {
      config.params = params
    }
  }

  return axiosInstance(config)
}

const getRequest = (endpoint, params = {}, options = {}) => makeRequest(endpoint, METHODS.get, params, {}, options)
const postRequest = (endpoint, data = {}, params = {}, options = {}) => makeRequest(endpoint, METHODS.post, params, data, options)
const putRequest = (endpoint, data = {}, params = {}, options = {}) => makeRequest(endpoint, METHODS.put, params, data, options)
const deleteRequest = (endpoint, data = {}, params = {}, options = {}) => makeRequest(endpoint, METHODS.delete, params, data, options)

export { getRequest, postRequest, putRequest, deleteRequest }
